/**
 * entreeRange — ancrer et retirer un mot-clé PAR PLAGE dans le JSON Lexical
 * d'une note, sans Lexical (C11 SP6, spec § 0.2 C et G).
 *
 * Miroir de `replaceNodesFromSelection` de l'éditeur du bureau
 * (`EntreePlugin/index.tsx`) : chaque nœud de texte ou nœud `entrees` traversé
 * par la plage est DÉCOUPÉ aux bornes et le mot-clé est AJOUTÉ à chaque
 * morceau — un nœud `entrees` le reçoit en plus des siens, un nœud de texte
 * devient `entrees` en gardant sa mise en forme. Les chevauchements sont donc
 * naturels : « travail vivant » [A] puis « vivant crée » [B] donnent
 * « travail  » [A], « vivant » [A, B], «  crée » [B].
 *
 * Invariant : le texte du bloc ne change jamais. Les offsets d'une plage sont
 * ceux du texte projeté du bloc (`content.text`, règles de
 * `api/src/utils/noteTextProjection.ts`, reprises ici par un mini-walker
 * autonome — ce module n'importe rien, car le serveur en reçoit une COPIE
 * octet pour octet : `api/src/utils/entreeRange.ts`, test de dérive
 * `entreeRange.drift.test.ts`). Toute modification ici se recopie là-bas.
 *
 * Depuis C22 SP2, un nœud `entrees` porte aussi `noteRefs[]` (notes reliées) :
 * chaque reconstruction le conserve, et un nœud ne redevient texte que sans
 * concept ni note reliée.
 */

export interface RangeEntree {
  id: string;
  cle: string;
  userId?: string;
  /**
   * Note d'importance (1–5) d'une NOUVELLE occurrence — le jugement humain
   * « à quel point ce passage explique le concept ». Absente → 3, comme le
   * geste « Relier » du bureau. Ignorée si `item` est fourni (le réancrage
   * conserve l'importance d'origine).
   */
  rating?: number;
  /**
   * Marqueur « piste » d'une NOUVELLE occurrence (C22 SP0 bis) : un lien à
   * revoir plus tard, indépendant de l'importance. Ignoré si `item` est fourni.
   */
  piste?: true;
  /**
   * Provenance d'une NOUVELLE occurrence (C22 SP1) : la personne qui pose la
   * marque (`auteurId`, profil courant) et le moment de la pose (`poseeLe`,
   * ISO 8601), préremplis pour l'affichage immédiat — le serveur estampille
   * de toute façon et a le dernier mot. Jamais inventés : absents, l'item n'en
   * porte pas. Ignorés si `item` est fourni (le réancrage conserve la
   * provenance d'origine).
   */
  auteurId?: string;
  poseeLe?: string;
  /**
   * Item d'occurrence à poser TEL QUEL (`key`, `rating`, `validated`,
   * `detectionMethod`, `userId`, `auteurId`, `poseeLe`) : le réancrage après
   * une édition (SP6 bis) conserve la marque d'origine. Absent → nouvelle
   * occurrence (`rating: 3` sauf `rating` ci-dessus, clé neuve,
   * `detectionMethod: 'manual'`, `validated: true` : une marque posée à la
   * main naît validée).
   */
  item?: Record<string, unknown>;
}

/** Une marque d'un bloc : une occurrence d'un mot-clé sur une suite contiguë de feuilles. */
export interface AnchoredMark {
  start: number;
  end: number;
  /** L'entrée avec son item d'origine — se passe directement à `tagRangeInJSON`. */
  entree: RangeEntree;
}

export interface BlockRange {
  /** Index de l'enfant du root. */
  block: number;
  start: number;
  end: number;
}

export interface BlockPoint {
  block: number;
  offset: number;
}

/** Relation d'un passage avec la note à laquelle il est relié (C22 SP2) ; absente = lien neutre. */
export type RelationNote = 'soutient' | 'oppose' | 'suite';

/**
 * Une note reliée à un passage (C22 SP2) : item de `noteRefs[]` sur un nœud
 * `entrees`. Le passage est l'ensemble des feuilles qui portent le même couple
 * (`noteId`, `key`). Provenance à plat, mêmes noms que SP1.
 */
export interface NoteRefItem {
  noteId: string;
  noteTitle: string;
  key: string;
  relation?: RelationNote;
  justification?: string;
  origine: 'manuelle' | 'reperage' | 'assistant' | 'import';
  auteurId?: string;
  poseeLe?: string;
  validated: boolean;
}

type Node = Record<string, unknown>;
type Container = Node & { children: unknown[] };

// ─── Règles de projection — copiées de api/src/utils/noteTextProjection.ts ──
const INLINE_CONTAINER_TYPES: ReadonlySet<string> = new Set(['source-line', 'link', 'autolink', 'mark', 'hashtag']);
const LINE_ANCHOR_TYPES: ReadonlySet<string> = new Set(['source-line']);

const asNode = (n: unknown): Node | null => (n && typeof n === 'object' ? (n as Node) : null);
const isContainer = (n: Node | null): n is Container => !!n && Array.isArray(n.children);
const isTextBearing = (n: Node): boolean => typeof n.text === 'string' && !isContainer(n);
const isLineAnchor = (n: Node | null): boolean => {
  if (!n) return false;
  if (typeof n.type === 'string' && LINE_ANCHOR_TYPES.has(n.type)) return true;
  const sourceData = asNode(n.sourceData);
  return !!sourceData && typeof sourceData.lineNumber === 'number';
};
const startsNewBlock = (n: Node | null): boolean => isContainer(n) && !(typeof n.type === 'string' && INLINE_CONTAINER_TYPES.has(n.type));

function decoratorText(n: Node): string {
  switch (n.type) {
    case 'linebreak':
    case 'horizontalrule':
      return '\n';
    case 'note-reference':
      return `[[${typeof n.noteTitle === 'string' ? n.noteTitle : ''}]]`;
    case 'equation':
      return typeof n.equation === 'string' ? n.equation : '';
    case 'tweet':
      return `https://twitter.com/i/web/status/${typeof n.id === 'string' ? n.id : ''}`;
    case 'youtube':
      return `https://www.youtube.com/watch?v=${typeof n.videoID === 'string' ? n.videoID : ''}`;
    default:
      return '';
  }
}

// ─── Mini-walker : les feuilles d'un bloc avec leurs offsets ────────────────

interface Leaf {
  node: Node;
  parent: Container;
  index: number;
  offset: number;
  length: number;
}

interface Walk {
  text: string;
  leaves: Leaf[];
}

function walkNode(raw: unknown, parent: Container | null, index: number, w: Walk): void {
  const n = asNode(raw);
  if (!n) return;
  if (isTextBearing(n)) {
    const text = n.text as string;
    if (text.length > 0 && parent) w.leaves.push({ node: n, parent, index, offset: w.text.length, length: text.length });
    w.text += text;
    return;
  }
  if (!isContainer(n)) {
    w.text += decoratorText(n);
    return;
  }
  for (let i = 0; i < n.children.length; i++) {
    const child = asNode(n.children[i]);
    const previous = i > 0 ? asNode(n.children[i - 1]) : null;
    if (i > 0 && startsNewBlock(previous)) {
      w.text += '\n\n';
    } else if (i > 0 && isLineAnchor(previous) && isLineAnchor(child) && w.text.length > 0) {
      const chunk: Walk = { text: '', leaves: [] };
      walkNode(child, null, 0, chunk);
      if (chunk.text.length > 0 && !/\s$/.test(w.text) && !/^\s/.test(chunk.text)) w.text += ' ';
    }
    walkNode(child, n, i, w);
  }
}

const rootOf = (json: unknown): Container | null => {
  const root = asNode(asNode(json)?.root);
  return isContainer(root) ? root : null;
};

function walkBlock(json: unknown, block: number): Walk | null {
  const root = rootOf(json);
  const child = root ? asNode(root.children[block]) : null;
  if (!root || !child) return null;
  const w: Walk = { text: '', leaves: [] };
  walkNode(child, root, block, w);
  return w;
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

// ─── Nœuds `entrees` ────────────────────────────────────────────────────────

const isEntreeNode = (n: Node): boolean => n.type === 'entrees' || n.type === 'entree';
/** Un nœud texte ordinaire scindable (un `token` / `segmented` est atomique côté Lexical). */
const isPlainText = (n: Node): boolean => n.type === 'text' && (n.mode === undefined || n.mode === 'normal');

const TEXT_FIELDS = ['detail', 'format', 'mode', 'style', 'version'] as const;

function textFields(from: Node): Node {
  const out: Node = {};
  for (const f of TEXT_FIELDS) if (from[f] !== undefined) out[f] = from[f];
  if (out.detail === undefined) out.detail = 0;
  if (out.format === undefined) out.format = 0;
  if (out.mode === undefined) out.mode = 'normal';
  if (out.style === undefined) out.style = '';
  if (out.version === undefined) out.version = 1;
  return out;
}

/** Même générateur que `insertEntreeNode.generateEntreeKey` (identifiant d'occurrence). */
function generateKey(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/** Items d'un nœud `entrees` / `entree` — les deux formats, dédoublonnés par id. */
function itemsOf(n: Node): Node[] {
  const raw: unknown[] = Array.isArray(n.entrees) && n.entrees.length > 0 ? n.entrees
    : Array.isArray(n.entreeList) && n.entreeList.length > 0 ? n.entreeList : [n];
  const out: Node[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const e = asNode(item);
    if (!e) continue;
    const id = typeof e.id === 'string' ? e.id : typeof e._id === 'string' ? e._id : '';
    const cle = typeof e.cle === 'string' ? e.cle : '';
    if (!id || !cle || seen.has(id)) continue;
    seen.add(id);
    const kept: Node = { ...e, id, cle };
    delete kept._id;
    if (e === n) {
      // Type historique : ne garder que les champs de l'item, pas ceux du nœud texte.
      const { cle: c, id: i, rating, validated, key, detectionMethod, userId, piste, auteurId, poseeLe } = e;
      const item: Node = { cle: c, id: i };
      if (rating !== undefined) item.rating = rating;
      if (validated !== undefined) item.validated = validated;
      if (key !== undefined) item.key = key;
      if (detectionMethod !== undefined) item.detectionMethod = detectionMethod;
      if (userId !== undefined) item.userId = userId;
      if (piste !== undefined) item.piste = piste;
      if (auteurId !== undefined) item.auteurId = auteurId;
      if (poseeLe !== undefined) item.poseeLe = poseeLe;
      out.push(item);
    } else {
      out.push(kept);
    }
  }
  return out;
}

const idsOf = (n: Node): string[] => itemsOf(n).map(item => item.id as string);

/** Notes reliées d'un nœud marqué : items valides (`noteId` et `key`), dédoublonnés, copiés. */
function noteRefsOf(n: Node): Node[] {
  if (!isEntreeNode(n) || !Array.isArray(n.noteRefs)) return [];
  const out: Node[] = [];
  const seen = new Set<string>();
  for (const raw of n.noteRefs) {
    const r = asNode(raw);
    const noteId = r && typeof r.noteId === 'string' ? r.noteId : '';
    const key = r && typeof r.key === 'string' ? r.key : '';
    const identite = `${noteId}\u0000${key}`;
    if (!r || !noteId || !key || seen.has(identite)) continue;
    seen.add(identite);
    out.push({ ...r });
  }
  return out;
}

function newItem(entree: RangeEntree): Node {
  if (entree.item) {
    const kept: Node = { ...entree.item, cle: entree.cle, id: entree.id };
    if (entree.userId && kept.userId === undefined) kept.userId = entree.userId;
    return kept;
  }
  // Une marque posée à la main naît validée (C22 SP1, spec § 2.2) ; la provenance
  // n'est recopiée que si l'appelant la fournit — jamais inventée ici.
  const item: Node = { cle: entree.cle, id: entree.id, rating: entree.rating ?? 3, validated: true, key: generateKey(), detectionMethod: 'manual' };
  if (entree.userId) item.userId = entree.userId;
  if (entree.piste === true) item.piste = true;
  if (typeof entree.auteurId === 'string' && entree.auteurId !== '') item.auteurId = entree.auteurId;
  if (typeof entree.poseeLe === 'string' && entree.poseeLe !== '') item.poseeLe = entree.poseeLe;
  return item;
}

/**
 * Nœud `entrees` (format moderne) portant `items` (concepts) et `noteRefs`
 * (notes reliées, C22 SP2) sur `text`, mise en forme de `base`. `entrees` et
 * `entreeList` sont toujours écrits ; `noteRefs` seulement s'il n'est pas vide.
 */
function buildEntreesNode(text: string, items: Node[], base: Node, noteRefs: Node[] = []): Node {
  const node: Node = { type: 'entrees', ...textFields(base), text };
  node.entreeList = items.map(item => ({ cle: item.cle, id: item.id }));
  node.entrees = items;
  const owner = items.find(item => typeof item.userId === 'string');
  if (owner) node.userId = owner.userId;
  if (noteRefs.length > 0) node.noteRefs = noteRefs;
  return node;
}

/** Nœud marqué s'il reste un concept ou une note reliée, texte ordinaire sinon. */
function buildMarkedNode(text: string, items: Node[], noteRefs: Node[], base: Node): Node {
  return items.length > 0 || noteRefs.length > 0 ? buildEntreesNode(text, items, base, noteRefs) : buildTextNode(text, base);
}

function buildTextNode(text: string, base: Node): Node {
  return { type: 'text', ...textFields(base), text };
}

/** Le même nœud, sur un autre texte (préfixe ou suffixe d'une découpe). */
function sliceNode(n: Node, text: string): Node {
  if (isEntreeNode(n)) return buildMarkedNode(text, itemsOf(n), noteRefsOf(n), n);
  return { ...n, text };
}

// ─── Ancrer ─────────────────────────────────────────────────────────────────

/**
 * Ancre `entree` sur la plage `range` (offsets du texte projeté du bloc) :
 * découpe aux bornes et cumul par morceau. Un morceau qui porte déjà ce
 * mot-clé est laissé tel quel. `changed: false` si rien n'a été ajouté (plage
 * vide, hors du bloc, ou déjà portée partout) — le JSON rendu est alors l'original.
 */
export function tagRangeInJSON<T>(json: T, range: BlockRange, entree: RangeEntree): { json: T; changed: boolean } {
  const walk = walkBlock(json, range.block);
  if (!walk) return { json, changed: false };
  const start = Math.max(0, Math.min(walk.text.length, Math.floor(Math.min(range.start, range.end))));
  const end = Math.max(0, Math.min(walk.text.length, Math.floor(Math.max(range.start, range.end))));
  if (end <= start) return { json, changed: false };

  const touched = walk.leaves.filter(leaf => leaf.offset < end && leaf.offset + leaf.length > start
    && (isPlainText(leaf.node) || isEntreeNode(leaf.node)) && !idsOf(leaf.node).includes(entree.id));
  if (touched.length === 0) return { json, changed: false };

  const out = clone(json);
  const copy = walkBlock(out, range.block);
  if (!copy) return { json, changed: false };
  const wanted = new Set(touched.map(leaf => `${leaf.offset}:${leaf.length}`));
  // Une seule occurrence (même `key`) pour tous les morceaux de la plage, comme l'éditeur du bureau.
  const item = newItem(entree);
  // Remplacer de la dernière feuille à la première : les indices des précédentes restent valides.
  const targets = copy.leaves.filter(leaf => wanted.has(`${leaf.offset}:${leaf.length}`)).reverse();
  for (const leaf of targets) {
    const text = leaf.node.text as string;
    const s = Math.max(0, start - leaf.offset);
    const e = Math.min(text.length, end - leaf.offset);
    const items = [...(isEntreeNode(leaf.node) ? itemsOf(leaf.node) : []), { ...item }];
    const pieces: Node[] = [];
    if (s > 0) pieces.push(sliceNode(leaf.node, text.slice(0, s)));
    pieces.push(buildEntreesNode(text.slice(s, e), items, leaf.node, noteRefsOf(leaf.node)));
    if (e < text.length) pieces.push(sliceNode(leaf.node, text.slice(e)));
    leaf.parent.children.splice(leaf.index, 1, ...pieces);
  }
  return { json: out, changed: true };
}

// ─── Retirer ────────────────────────────────────────────────────────────────

/**
 * Fusionne, dans tout l'arbre, les voisins de même mise en forme (copie) :
 * texte avec texte, et marques IDENTIQUES entre elles (mêmes items, mêmes
 * clés d'occurrence — les deux moitiés d'une découpe dont l'autre mot-clé a
 * été retiré). Deux marques différentes ne sont jamais fusionnées.
 */
export function mergeAdjacentText<T>(json: T): T {
  const out = clone(json);
  const sameStyle = (a: Node, b: Node): boolean => TEXT_FIELDS.every(f => textFields(a)[f] === textFields(b)[f]);
  const sameItems = (a: Node, b: Node): boolean => JSON.stringify(itemsOf(a)) === JSON.stringify(itemsOf(b))
    && JSON.stringify(noteRefsOf(a)) === JSON.stringify(noteRefsOf(b));
  const mergeable = (a: Node, b: Node): boolean => sameStyle(a, b)
    && ((isPlainText(a) && isPlainText(b)) || (isEntreeNode(a) && isEntreeNode(b) && sameItems(a, b)));
  const visit = (n: Node | null): void => {
    if (!isContainer(n)) return;
    for (let i = 1; i < n.children.length; i++) {
      const prev = asNode(n.children[i - 1]);
      const cur = asNode(n.children[i]);
      if (prev && cur && mergeable(prev, cur)) {
        const text = `${prev.text as string}${cur.text as string}`;
        n.children[i - 1] = isEntreeNode(prev) ? buildEntreesNode(text, itemsOf(prev), prev, noteRefsOf(prev)) : { ...prev, text };
        n.children.splice(i, 1);
        i--;
      }
    }
    for (const child of n.children) visit(asNode(child));
  };
  visit(rootOf(out));
  return out;
}

/** Clé d'occurrence normalisée : une clé absente (données anciennes) vaut `''`, comme dans `marksInBlock`. */
const occurrenceKey = (item: Node): string => (typeof item.key === 'string' ? item.key : '');

/**
 * Bornes (indices de feuilles) de LA marque de `entreeId` autour du point :
 * même mot-clé, même clé d'occurrence, feuilles contiguës — la règle de
 * `marksInBlock`. `null` si le point ne porte pas ce mot-clé.
 */
function markSpan(walk: Walk, entreeId: string, offset: number): { from: number; to: number } | null {
  const itemAt = (leaf: Leaf): Node | undefined => (isEntreeNode(leaf.node) ? itemsOf(leaf.node).find(item => item.id === entreeId) : undefined);
  const hit = walk.leaves.findIndex(leaf => offset >= leaf.offset && offset < leaf.offset + leaf.length);
  const target = hit < 0 ? undefined : itemAt(walk.leaves[hit]);
  if (!target) return null;
  const key = occurrenceKey(target);
  const sameMark = (leaf: Leaf): boolean => {
    const item = itemAt(leaf);
    return !!item && occurrenceKey(item) === key;
  };
  let from = hit;
  while (from > 0 && sameMark(walk.leaves[from - 1])) from--;
  let to = hit;
  while (to + 1 < walk.leaves.length && sameMark(walk.leaves[to + 1])) to++;
  return { from, to };
}

/**
 * Pose la note d'importance `rating` sur la suite CONTIGUË de feuilles qui
 * portent `entreeId` avec la même clé d'occurrence autour du point — « sa »
 * marque, cette occurrence-là (une occurrence accolée du même mot-clé, d'une
 * autre clé, ne bouge pas).
 * Les autres occurrences du même mot-clé dans la note ne bougent pas :
 * l'importance se juge passage par passage, comme le panneau « Entrées » du
 * bureau. Le texte du bloc ne change pas ; les voisins ne sont pas refusionnés
 * (deux occurrences gardent leur clé, donc restent distinctes).
 * `changed: false` si le point ne porte pas ce mot-clé, ou si l'importance y
 * est déjà celle-là.
 */
export function setRatingInJSON<T>(json: T, entreeId: string, point: BlockPoint, rating: number): { json: T; changed: boolean } {
  const walk = walkBlock(json, point.block);
  if (!walk) return { json, changed: false };
  const span = markSpan(walk, entreeId, point.offset);
  if (!span) return { json, changed: false };

  const out = clone(json);
  const copy = walkBlock(out, point.block);
  if (!copy) return { json, changed: false };
  let changed = false;
  for (let i = span.from; i <= span.to; i++) {
    const leaf = copy.leaves[i];
    let touched = false;
    const items = itemsOf(leaf.node).map(item => {
      if (item.id !== entreeId || item.rating === rating) return item;
      touched = true;
      return { ...item, rating };
    });
    if (!touched) continue;
    changed = true;
    leaf.parent.children[leaf.index] = buildEntreesNode(leaf.node.text as string, items, leaf.node, noteRefsOf(leaf.node));
  }
  return changed ? { json: out, changed: true } : { json, changed: false };
}

/**
 * Pose ou lève le marqueur « piste » (C22 SP0 bis) sur la marque qui porte
 * `entreeId` autour du point — cette occurrence-là, c'est-à-dire la suite
 * CONTIGUË de feuilles qui portent ce mot-clé avec la MÊME clé d'occurrence
 * (comme `marksInBlock` : deux marques accolées du même mot-clé restent
 * distinctes). `true` écrit `piste: true` ; `false` RETIRE le champ : la
 * donnée ne porte jamais `piste: false`, et une valeur étrangère est nettoyée.
 * Ni l'importance, ni `validated`, ni le texte ne changent. `changed: false`
 * si le point ne porte pas ce mot-clé, ou si l'état y est déjà celui-là.
 */
export function setPisteInJSON<T>(json: T, entreeId: string, point: BlockPoint, piste: boolean): { json: T; changed: boolean } {
  const walk = walkBlock(json, point.block);
  if (!walk) return { json, changed: false };
  const span = markSpan(walk, entreeId, point.offset);
  if (!span) return { json, changed: false };

  const out = clone(json);
  const copy = walkBlock(out, point.block);
  if (!copy) return { json, changed: false };
  let changed = false;
  for (let i = span.from; i <= span.to; i++) {
    const leaf = copy.leaves[i];
    let touched = false;
    const items = itemsOf(leaf.node).map(item => {
      if (item.id !== entreeId) return item;
      if (piste ? item.piste === true : item.piste === undefined) return item;
      touched = true;
      const next: Node = { ...item };
      delete next.piste;
      if (piste) next.piste = true;
      return next;
    });
    if (!touched) continue;
    changed = true;
    leaf.parent.children[leaf.index] = buildEntreesNode(leaf.node.text as string, items, leaf.node, noteRefsOf(leaf.node));
  }
  return changed ? { json: out, changed: true } : { json, changed: false };
}

/**
 * Retire `entreeId` de la suite CONTIGUË de feuilles qui le portent avec la
 * même clé d'occurrence autour du point (la suite qui forme « sa » marque :
 * une occurrence accolée du même mot-clé, d'une autre clé, reste) ; une feuille qui ne porte plus rien
 * redevient du texte, et les nœuds texte voisins de même mise en forme sont
 * refusionnés. `stillAnchored` : le mot-clé reste ancré ailleurs dans la note.
 */
export function untagEntreeInJSON<T>(json: T, entreeId: string, point: BlockPoint): { json: T; changed: boolean; stillAnchored: boolean } {
  const walk = walkBlock(json, point.block);
  const still = (value: T): boolean => entreeIdsInJSON(value).includes(entreeId);
  if (!walk) return { json, changed: false, stillAnchored: still(json) };
  const span = markSpan(walk, entreeId, point.offset);
  if (!span) return { json, changed: false, stillAnchored: still(json) };

  const out = clone(json);
  const copy = walkBlock(out, point.block);
  if (!copy) return { json, changed: false, stillAnchored: still(json) };
  for (let i = span.from; i <= span.to; i++) {
    const leaf = copy.leaves[i];
    const remaining = itemsOf(leaf.node).filter(item => item.id !== entreeId);
    const text = leaf.node.text as string;
    leaf.parent.children[leaf.index] = buildMarkedNode(text, remaining, noteRefsOf(leaf.node), leaf.node);
  }
  const merged = mergeAdjacentText(out);
  return { json: merged, changed: true, stillAnchored: still(merged) };
}

/**
 * Retire `entreeId` de TOUTES ses marques dans la note (C11 SP6 ter : « Retirer
 * de la note » depuis la chip d'un mot-clé) — bloc par bloc, marque par marque,
 * par `untagEntreeInJSON`. `changed: false` si le mot-clé n'était ancré nulle
 * part (mot-clé posé sur la note seule, dans `cles` sans marque).
 */
export function untagEntreeEverywhere<T>(json: T, entreeId: string): { json: T; changed: boolean } {
  const root = rootOf(json);
  if (!root) return { json, changed: false };
  let out = json;
  let changed = false;
  for (let block = 0; block < root.children.length; block++) {
    for (;;) {
      const mark = marksInBlock(out, block).find(m => m.entree.id === entreeId);
      if (!mark) break;
      const result = untagEntreeInJSON(out, entreeId, { block, offset: mark.start });
      if (!result.changed) break;
      out = result.json;
      changed = true;
    }
  }
  return { json: out, changed };
}

// ─── Lire ───────────────────────────────────────────────────────────────────

/** Ids des mots-clés ancrés dans la note, dédoublonnés, dans l'ordre du document. */
export function entreeIdsInJSON(json: unknown): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const visit = (n: Node | null): void => {
    if (!n) return;
    if (isEntreeNode(n)) for (const id of idsOf(n)) if (!seen.has(id)) { seen.add(id); out.push(id); }
    if (isContainer(n)) for (const child of n.children) visit(asNode(child));
  };
  visit(rootOf(json));
  return out;
}

/** Feuilles marquées de la note, avec le rang de leur bloc (parcours en profondeur). */
function marquesEnFeuilles(json: unknown): Array<{ node: Node; parent: Container; index: number; block: number }> {
  const out: Array<{ node: Node; parent: Container; index: number; block: number }> = [];
  const root = rootOf(json);
  if (!root) return out;
  const visit = (n: Node | null, parent: Container | null, index: number, block: number): void => {
    if (!n) return;
    if (isEntreeNode(n) && isTextBearing(n) && parent) out.push({ node: n, parent, index, block });
    if (isContainer(n)) n.children.forEach((child, k) => visit(asNode(child), n, k, block));
  };
  root.children.forEach((child, k) => visit(asNode(child), root, k, k));
  return out;
}

/**
 * Nombre de marques (occurrences) d'un concept dans la note : une occurrence est
 * une suite de feuilles d'un même bloc qui portent le concept avec la même clé ;
 * une feuille sans clé compte pour une occurrence à elle seule.
 */
export function marquesDe(json: unknown, entreeId: string): number {
  const vues = new Set<string>();
  let sansCle = 0;
  for (const feuille of marquesEnFeuilles(json)) {
    for (const item of itemsOf(feuille.node)) {
      if (item.id !== entreeId) continue;
      if (typeof item.key === 'string' && item.key !== '') vues.add(`${feuille.block}\u0000${item.key}`);
      else sansCle++;
    }
  }
  return vues.size + sansCle;
}

/**
 * Réécrit, dans toutes les marques de la note, le concept `deId` en `versId` /
 * `versCle` (C31 SP2 « Réunir », spec § 4.1 étape 1) : chaque item garde tout le
 * reste — importance, piste, clé d'occurrence, `validated`, `detectionMethod`,
 * provenance. Une feuille qui porte déjà `versId` garde UN item, celui de
 * `versId` (sa provenance), avec l'importance la plus forte et `piste` si l'un
 * des deux l'a. Les `noteRefs` restent ; le texte ne bouge pas ; les voisins
 * devenus identiques sont refondus. Sans marque de `deId`, le même objet est
 * rendu, `marques: 0`.
 */
export function remplacerConceptDansJSON<T>(json: T, deId: string, versId: string, versCle: string): { json: T; marques: number } {
  const marques = marquesDe(json, deId);
  if (marques === 0 || deId === versId) return { json, marques: deId === versId ? 0 : marques };
  const out = clone(json);
  for (const feuille of marquesEnFeuilles(out)) {
    const items = itemsOf(feuille.node);
    const de = items.find(item => item.id === deId);
    if (!de) continue;
    const vers = items.find(item => item.id === versId);
    const reecrits: Node[] = [];
    for (const item of items) {
      if (item.id === versId) {
        const fusionne: Node = { ...item };
        const rd = typeof de.rating === 'number' ? de.rating : -Infinity;
        const rv = typeof item.rating === 'number' ? item.rating : -Infinity;
        if (rd > rv) fusionne.rating = de.rating;
        if (de.piste === true) fusionne.piste = true;
        reecrits.push(fusionne);
      } else if (item.id === deId) {
        if (!vers) reecrits.push({ ...item, id: versId, cle: versCle });
      } else {
        reecrits.push(item);
      }
    }
    feuille.parent.children[feuille.index] = buildEntreesNode(feuille.node.text as string, reecrits, feuille.node, noteRefsOf(feuille.node));
  }
  return { json: mergeAdjacentText(out), marques };
}

/**
 * Déplace les occurrences `keys` du concept `deId` vers `versId` / `versCle`
 * (C31 bis SP3 « Scinder », spec § 3.1 étape 2) : dans chaque feuille dont un
 * item porte `(deId, key ∈ keys)`, l'item est COPIÉ avec `id = versId`,
 * `cle = versCle`, tout le reste intact — la `key` surtout (les notes reliées et
 * les questions de mot la lisent), `rating`, `piste`, `validated`,
 * `detectionMethod`, provenance. Si la feuille porte déjà `versId`, un seul item
 * reste, celui de `versId` (règle de `remplacerConceptDansJSON`). Les autres
 * occurrences de `deId` restent ; les `noteRefs` et le texte ne bougent pas ;
 * les voisins devenus identiques sont refondus. Une clé vide n'est jamais
 * déplacée (marque ancienne). `dejaFaites` : clés demandées trouvées sous
 * `versId` et plus sous `deId` (reprise d'un geste interrompu) ; `introuvables` :
 * clés absentes des deux. Sans déplacement, le même objet est rendu.
 */
export function deplacerMarquesDansJSON<T>(json: T, deId: string, keys: ReadonlyArray<string>, versId: string, versCle: string): { json: T; deplacees: number; dejaFaites: number; introuvables: string[] } {
  const rien = (introuvables: string[], dejaFaites = 0) => ({ json, deplacees: 0, dejaFaites, introuvables });
  const demandees = Array.from(new Set(keys));
  if (deId === versId || demandees.length === 0) return rien([]);
  const sousDe = new Set<string>();
  const sousVers = new Set<string>();
  for (const feuille of marquesEnFeuilles(json)) {
    for (const it of itemsOf(feuille.node)) {
      const key = occurrenceKey(it);
      if (key === '') continue;
      if (it.id === deId) sousDe.add(key);
      else if (it.id === versId) sousVers.add(key);
    }
  }
  const aDeplacer = new Set(demandees.filter(k => k !== '' && sousDe.has(k)));
  const dejaFaites = demandees.filter(k => !aDeplacer.has(k) && sousVers.has(k)).length;
  const introuvables = demandees.filter(k => !aDeplacer.has(k) && !sousVers.has(k));
  if (aDeplacer.size === 0) return rien(introuvables, dejaFaites);

  const out = clone(json);
  for (const feuille of marquesEnFeuilles(out)) {
    const items = itemsOf(feuille.node);
    const de = items.find(it => it.id === deId && aDeplacer.has(occurrenceKey(it)));
    if (!de) continue;
    const vers = items.find(it => it.id === versId);
    const reecrits: Node[] = [];
    for (const it of items) {
      if (it.id === versId) {
        const fusionne: Node = { ...it };
        const rd = typeof de.rating === 'number' ? de.rating : -Infinity;
        const rv = typeof it.rating === 'number' ? it.rating : -Infinity;
        if (rd > rv) fusionne.rating = de.rating;
        if (de.piste === true) fusionne.piste = true;
        reecrits.push(fusionne);
      } else if (it === de) {
        if (!vers) reecrits.push({ ...it, id: versId, cle: versCle });
      } else {
        reecrits.push(it);
      }
    }
    feuille.parent.children[feuille.index] = buildEntreesNode(feuille.node.text as string, reecrits, feuille.node, noteRefsOf(feuille.node));
  }
  return { json: mergeAdjacentText(out), deplacees: aDeplacer.size, dejaFaites, introuvables };
}

/**
 * Les marques d'un bloc : pour chaque occurrence (même `id`, même `key`)
 * portée par une suite CONTIGUË de feuilles, sa plage dans le texte projeté du
 * bloc et l'entrée avec son item d'origine. Deux mots-clés qui se chevauchent
 * donnent deux marques ; deux occurrences d'un même mot-clé séparées par du
 * texte (ou par une clé différente) aussi. Ordre : début puis fin croissants.
 */
export function marksInBlock(json: unknown, block: number): AnchoredMark[] {
  const walk = walkBlock(json, block);
  if (!walk) return [];
  const out: AnchoredMark[] = [];
  const latest = new Map<string, AnchoredMark>();
  for (const leaf of walk.leaves) {
    if (!isEntreeNode(leaf.node)) continue;
    for (const item of itemsOf(leaf.node)) {
      const id = item.id as string;
      const occurrence = `${id}\u0000${typeof item.key === 'string' ? item.key : ''}`;
      const current = latest.get(occurrence);
      if (current && current.end === leaf.offset) { current.end = leaf.offset + leaf.length; continue; }
      const entree: RangeEntree = { id, cle: item.cle as string, item: { ...item } };
      if (typeof item.userId === 'string') entree.userId = item.userId;
      const mark: AnchoredMark = { start: leaf.offset, end: leaf.offset + leaf.length, entree };
      latest.set(occurrence, mark);
      out.push(mark);
    }
  }
  return out.sort((a, b) => a.start - b.start || a.end - b.end);
}

/** Texte projeté de chaque bloc (enfant du root), sans séparateur. */
export function blockTexts(json: unknown): string[] {
  const root = rootOf(json);
  if (!root) return [];
  return root.children.map((_, i) => walkBlock(json, i)?.text ?? '');
}

/** Première plage dont le texte égale `text`, à partir de `from` (bloc, offset) ; `null` sinon. */
export function findTextRange(json: unknown, text: string, from: BlockPoint = { block: 0, offset: 0 }): BlockRange | null {
  if (!text) return null;
  const texts = blockTexts(json);
  for (let block = Math.max(0, from.block); block < texts.length; block++) {
    const idx = texts[block].indexOf(text, block === from.block ? from.offset : 0);
    if (idx >= 0) return { block, start: idx, end: idx + text.length };
  }
  return null;
}

// ─── Notes reliées (C22 SP2) ────────────────────────────────────────────────

export interface NoteRefInit {
  noteId: string;
  noteTitle: string;
  relation?: RelationNote;
  justification?: string;
  /** Absente → `manuelle` (une personne relie). */
  origine?: NoteRefItem['origine'];
  /** Préremplis pour l'affichage immédiat ; le serveur estampille et a le dernier mot. */
  auteurId?: string;
  poseeLe?: string;
  /** Item à poser TEL QUEL (réancrage après édition, réécriture) : clé et provenance conservées. */
  item?: Record<string, unknown>;
}

/** Une note reliée telle qu'on la lit : son item, son texte, ses plages par bloc. */
export interface NoteReliee {
  noteId: string;
  key: string;
  item: Record<string, unknown>;
  /** Texte du passage ; les morceaux non contigus sont joints par « … ». */
  texte: string;
  blocs: BlockRange[];
}

export const JUSTIFICATION_MAX = 500;
const RELATIONS: ReadonlyArray<string> = ['soutient', 'oppose', 'suite'];

const cleanRelation = (value: unknown): RelationNote | undefined =>
  (typeof value === 'string' && RELATIONS.includes(value) ? (value as RelationNote) : undefined);
const cleanJustification = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const text = value.trim().slice(0, JUSTIFICATION_MAX);
  return text === '' ? undefined : text;
};

function newNoteRef(init: NoteRefInit): Node {
  if (init.item) return { ...init.item, noteId: init.noteId, noteTitle: init.noteTitle };
  const origine = init.origine ?? 'manuelle';
  const item: Node = { noteId: init.noteId, noteTitle: init.noteTitle, key: generateKey(), origine, validated: origine === 'manuelle' || origine === 'import' };
  const relation = cleanRelation(init.relation);
  if (relation) item.relation = relation;
  const justification = cleanJustification(init.justification);
  if (justification) item.justification = justification;
  if (typeof init.auteurId === 'string' && init.auteurId !== '') item.auteurId = init.auteurId;
  if (typeof init.poseeLe === 'string' && init.poseeLe !== '') item.poseeLe = init.poseeLe;
  return item;
}

/**
 * Relie les plages `ranges` (offsets du texte projeté de chaque bloc) à une
 * note : découpe aux bornes, UN item (une clé) pour toutes les plages. Une
 * feuille déjà reliée à cette note est laissée telle quelle. `changed: false`
 * (et le JSON d'origine) si rien n'a été ajouté.
 */
export function relierNoteInJSON<T>(json: T, ranges: BlockRange[], init: NoteRefInit): { json: T; changed: boolean; key: string } {
  const rien = { json, changed: false, key: '' };
  if (!init.noteId) return rien;
  const item = newNoteRef(init);
  const key = typeof item.key === 'string' ? item.key : '';
  if (!key) return rien;
  const out = clone(json);
  let changed = false;
  for (const range of ranges) {
    const walk = walkBlock(out, range.block);
    if (!walk) continue;
    const start = Math.max(0, Math.min(walk.text.length, Math.floor(Math.min(range.start, range.end))));
    const end = Math.max(0, Math.min(walk.text.length, Math.floor(Math.max(range.start, range.end))));
    if (end <= start) continue;
    // De la dernière feuille à la première : les indices des précédentes restent valides.
    const targets = walk.leaves.filter(leaf => leaf.offset < end && leaf.offset + leaf.length > start
      && (isPlainText(leaf.node) || isEntreeNode(leaf.node))
      && !noteRefsOf(leaf.node).some(ref => ref.noteId === init.noteId)).reverse();
    for (const leaf of targets) {
      const text = leaf.node.text as string;
      const s = Math.max(0, start - leaf.offset);
      const e = Math.min(text.length, end - leaf.offset);
      const marked = isEntreeNode(leaf.node);
      const refs = [...(marked ? noteRefsOf(leaf.node) : []), { ...item }];
      const pieces: Node[] = [];
      if (s > 0) pieces.push(sliceNode(leaf.node, text.slice(0, s)));
      pieces.push(buildEntreesNode(text.slice(s, e), marked ? itemsOf(leaf.node) : [], leaf.node, refs));
      if (e < text.length) pieces.push(sliceNode(leaf.node, text.slice(e)));
      leaf.parent.children.splice(leaf.index, 1, ...pieces);
      changed = true;
    }
  }
  return changed ? { json: out, changed: true, key } : rien;
}

/** Réécrit les `noteRefs` de chaque nœud marqué ; `rewrite` rend `null` pour « ne pas toucher ». */
function rewriteNoteRefs<T>(json: T, rewrite: (refs: Node[]) => Node[] | null): { json: T; changed: boolean } {
  const out = clone(json);
  let changed = false;
  const visit = (n: Node | null): void => {
    if (!isContainer(n)) return;
    for (let i = 0; i < n.children.length; i++) {
      const child = asNode(n.children[i]);
      if (!child) continue;
      if (isEntreeNode(child) && isTextBearing(child)) {
        const refs = noteRefsOf(child);
        if (refs.length === 0) continue;
        const next = rewrite(refs);
        if (next === null) continue;
        changed = true;
        n.children[i] = buildMarkedNode(child.text as string, itemsOf(child), next, child);
      } else {
        visit(child);
      }
    }
  };
  visit(rootOf(out));
  return changed ? { json: out, changed: true } : { json, changed: false };
}

/** Retire le lien (`noteId`, `key`) de toutes ses feuilles ; un nœud qui ne porte plus rien redevient texte. */
export function delierNoteInJSON<T>(json: T, noteId: string, key: string): { json: T; changed: boolean } {
  const vise = (ref: Node): boolean => ref.noteId === noteId && ref.key === key;
  const result = rewriteNoteRefs(json, refs => (refs.some(vise) ? refs.filter(ref => !vise(ref)) : null));
  return result.changed ? { json: mergeAdjacentText(result.json), changed: true } : result;
}

/**
 * Précise le lien (`noteId`, `key`) sur toutes ses feuilles : `relation` et
 * `justification`, chacune laissée telle quelle si absente du patch, retirée si
 * `null` (ou invalide, ou vide). Ni la clé ni la provenance ne changent.
 */
export function setNoteRefInJSON<T>(
  json: T, noteId: string, key: string,
  patch: { relation?: RelationNote | null; justification?: string | null },
): { json: T; changed: boolean } {
  return rewriteNoteRefs(json, refs => {
    let touched = false;
    const next = refs.map(ref => {
      if (ref.noteId !== noteId || ref.key !== key) return ref;
      const item: Node = { ...ref };
      let modifie = false;
      const poser = (champ: 'relation' | 'justification', valeur: string | undefined): void => {
        if (item[champ] === valeur) return;
        modifie = true;
        if (valeur === undefined) delete item[champ];
        else item[champ] = valeur;
      };
      if ('relation' in patch) poser('relation', cleanRelation(patch.relation));
      if ('justification' in patch) poser('justification', cleanJustification(patch.justification));
      if (!modifie) return ref;
      touched = true;
      return item;
    });
    return touched ? next : null;
  });
}

/** Les notes reliées de la note, dans l'ordre de leur première feuille ; une par couple (`noteId`, `key`). */
export function notesRelieesInJSON(json: unknown): NoteReliee[] {
  const root = rootOf(json);
  if (!root) return [];
  const out: NoteReliee[] = [];
  const parIdentite = new Map<string, { lien: NoteReliee; morceaux: string[] }>();
  for (let block = 0; block < root.children.length; block++) {
    const walk = walkBlock(json, block);
    if (!walk) continue;
    for (const leaf of walk.leaves) {
      for (const ref of noteRefsOf(leaf.node)) {
        const noteId = ref.noteId as string;
        const key = ref.key as string;
        const identite = `${noteId}\u0000${key}`;
        let entry = parIdentite.get(identite);
        if (!entry) {
          entry = { lien: { noteId, key, item: { ...ref }, texte: '', blocs: [] }, morceaux: [] };
          parIdentite.set(identite, entry);
          out.push(entry.lien);
        }
        const dernier = entry.lien.blocs[entry.lien.blocs.length - 1];
        const texte = leaf.node.text as string;
        if (dernier && dernier.block === block && dernier.end === leaf.offset) {
          dernier.end = leaf.offset + leaf.length;
          entry.morceaux[entry.morceaux.length - 1] += texte;
        } else {
          entry.lien.blocs.push({ block, start: leaf.offset, end: leaf.offset + leaf.length });
          entry.morceaux.push(texte);
        }
      }
    }
  }
  for (const { lien, morceaux } of parIdentite.values()) lien.texte = morceaux.map(m => m.trim()).filter(Boolean).join(' … ');
  return out;
}
